/**
 * Lost & Found — admin moderation view of the resident exchange.
 *
 * Pinterest-style card grid (photo thumbnail + title + category + location).
 * Filter tabs: All / Lost / Found / Resolved. Click card → side drawer with
 * full detail + photos + admin resolve/delete actions.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Trash2,
  CheckCircle2,
  PackageOpen,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { api, get, post, del, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { EmptyState } from '@/components/ui/empty-state';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { TabsPills, type TabsPillsOption } from '@/components/ui/tabs-pills';
import { formatDateTime } from '@/lib/format';

type LfKind = 'lost' | 'found';
type LfStatus = 'open' | 'resolved' | 'expired';
type LfCategory =
  | 'keys'
  | 'bag'
  | 'phone_electronics'
  | 'jewelry'
  | 'document'
  | 'clothing'
  | 'child_item'
  | 'pet'
  | 'other';

const CATEGORY_LABEL: Record<LfCategory, string> = {
  keys: 'Keys',
  bag: 'Bag / Wallet',
  phone_electronics: 'Phone / Electronics',
  jewelry: 'Jewelry',
  document: 'Document',
  clothing: 'Clothing',
  child_item: "Child's item",
  pet: 'Pet',
  other: 'Other'
};

const STATUS_LABEL: Record<LfStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
  expired: 'Expired'
};
const STATUS_VARIANT: Record<LfStatus, 'muted' | 'info' | 'warn' | 'ok' | 'alert'> = {
  open: 'info',
  resolved: 'ok',
  expired: 'muted'
};

interface LfItem {
  id: string;
  kind: LfKind;
  category: LfCategory;
  title: string;
  description: string | null;
  location_hint: string | null;
  incident_date: string | null;
  status: LfStatus;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolution_notes: string | null;
  posted_by_user_id: string;
  posted_by_kind: string;
  photo_storage_keys: string[];
  contact_name: string | null;
  contact_phone_visible: boolean;
  expires_at: string | null;
  created_at: string;
}

type FilterTab = 'all' | 'lost' | 'found' | 'resolved';

const TAB_ITEMS: TabsPillsOption[] = [
  { value: 'all', label: 'All' },
  { value: 'lost', label: 'Lost' },
  { value: 'found', label: 'Found' },
  { value: 'resolved', label: 'Resolved' }
];

const CATEGORY_OPTIONS: Array<{ value: LfCategory | 'all'; label: string }> = [
  { value: 'all', label: 'All categories' },
  ...(Object.entries(CATEGORY_LABEL).map(([v, l]) => ({ value: v as LfCategory, label: l })))
];


export function LostFoundPage(): JSX.Element {
  const qc = useQueryClient();
  const [tab, setTab] = useState<FilterTab>('all');
  const [category, setCategory] = useState<LfCategory | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [resolveTarget, setResolveTarget] = useState<LfItem | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<LfItem | null>(null);

  const params: Record<string, string | number> = { limit: 50 };
  if (tab === 'lost') params.kind = 'lost';
  if (tab === 'found') params.kind = 'found';
  if (tab === 'resolved') params.status = 'resolved';
  if (category !== 'all') params.category = category;
  if (searchTerm.trim()) params.q = searchTerm.trim();

  const listQuery = useQuery({
    queryKey: ['lost-found', tab, category, searchTerm],
    queryFn: () =>
      get<{ items: LfItem[]; next_cursor: string | null }>('/lost-found', { params })
  });
  const items = listQuery.data?.items ?? [];

  const detailQuery = useQuery({
    queryKey: ['lost-found', detailId],
    enabled: Boolean(detailId),
    queryFn: () => get<LfItem>(`/lost-found/${detailId}`)
  });

  const resolveMut = useMutation({
    mutationFn: (vars: { id: string; resolution_notes: string }) =>
      post<LfItem>(`/lost-found/${vars.id}/resolve`, {
        resolution_notes: vars.resolution_notes || undefined
      }),
    onSuccess: () => {
      toast.success('Item marked resolved');
      setResolveTarget(null);
      setResolveNotes('');
      void qc.invalidateQueries({ queryKey: ['lost-found'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => del<void>(`/lost-found/${id}`),
    onSuccess: () => {
      toast.success('Item deleted');
      setConfirmDelete(null);
      setDetailId(null);
      void qc.invalidateQueries({ queryKey: ['lost-found'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const openCount = items.filter((i) => i.status === 'open').length;
  const resolvedCount = items.filter((i) => i.status === 'resolved').length;

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Lost & Found"
        description="Resident-driven exchange for mislaid items. Admin can resolve or remove inappropriate posts. Items auto-expire after 30 days."
      />

      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
        {[
          ['Total', items.length],
          ['Open', openCount],
          ['Resolved', resolvedCount],
          ['Lost', items.filter((i) => i.kind === 'lost').length]
        ].map(([label, n]) => (
          <Card key={String(label)}>
            <CardContent className="p-vt-4">
              <div className="text-vt-12 text-vt-text-muted">{label}</div>
              <div className="mt-1 text-vt-24 font-semibold text-vt-text">{n}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-vt-3">
        <TabsPills options={TAB_ITEMS} value={tab} onChange={(v) => setTab(v as FilterTab)} />
        <Select
          value={category}
          onChange={(e) => setCategory(e.target.value as LfCategory | 'all')}
          className="max-w-[200px]"
        >
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-vt-text-muted" />
          <Input
            className="pl-9"
            placeholder="Search title, description, location"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {listQuery.isLoading ? (
        <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-56 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<PackageOpen className="h-6 w-6" />}
              title="No items found"
              description="As residents post lost / found items, they will show up here."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((i) => (
            <LostFoundCard key={i.id} item={i} onOpen={() => setDetailId(i.id)} />
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={Boolean(detailId)} onOpenChange={(o) => !o && setDetailId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{detailQuery.data?.title ?? 'Item detail'}</DialogTitle>
            <DialogDescription>
              {detailQuery.data ? (
                <>
                  <Badge variant={detailQuery.data.kind === 'lost' ? 'warn' : 'info'}>
                    {detailQuery.data.kind === 'lost' ? 'Lost' : 'Found'}
                  </Badge>{' '}
                  · {CATEGORY_LABEL[detailQuery.data.category]} ·{' '}
                  {formatDateTime(detailQuery.data.created_at)}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {detailQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : detailQuery.data ? (
            <LostFoundDetail item={detailQuery.data} />
          ) : null}
          <DialogFooter>
            {detailQuery.data?.status === 'open' ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setResolveTarget(detailQuery.data!);
                  setResolveNotes('');
                }}
              >
                <CheckCircle2 className="h-4 w-4" /> Mark resolved
              </Button>
            ) : null}
            {detailQuery.data ? (
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(detailQuery.data!)}
                className="text-vt-danger"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            ) : null}
            <Button onClick={() => setDetailId(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve dialog */}
      <Dialog open={Boolean(resolveTarget)} onOpenChange={(o) => !o && setResolveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark item as resolved</DialogTitle>
            <DialogDescription>
              Capture how this {resolveTarget?.kind === 'lost' ? 'lost' : 'found'} item was
              reunited (optional).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
            rows={3}
            placeholder="e.g. Owner contacted, picked up from security desk."
            maxLength={500}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                resolveTarget &&
                resolveMut.mutate({ id: resolveTarget.id, resolution_notes: resolveNotes })
              }
              disabled={resolveMut.isPending}
            >
              {resolveMut.isPending ? 'Saving…' : 'Mark resolved'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Delete this item?"
        description={`Permanently remove "${confirmDelete?.title ?? ''}" from the lost & found feed. This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => { if (confirmDelete) deleteMut.mutate(confirmDelete.id); }}
      />
    </div>
  );
}

interface CardProps {
  item: LfItem;
  onOpen: () => void;
}

function LostFoundCard({ item, onOpen }: CardProps): JSX.Element {
  const photoCount = item.photo_storage_keys?.length ?? 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface text-left transition-colors duration-vt hover:bg-vt-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
    >
      <div className="relative aspect-[4/3] w-full bg-vt-surface-2">
        {photoCount > 0 ? (
          <AuthedThumb itemId={item.id} idx={0} alt={item.title} />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-vt-text-muted">
            <HelpCircle className="h-8 w-8" />
          </div>
        )}
        <div className="absolute left-2 top-2">
          <Badge variant={item.kind === 'lost' ? 'warn' : 'info'}>
            {item.kind === 'lost' ? 'Lost' : 'Found'}
          </Badge>
        </div>
        <div className="absolute right-2 top-2">
          <Badge variant={STATUS_VARIANT[item.status]}>{STATUS_LABEL[item.status]}</Badge>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-vt-1 p-vt-3">
        <div className="truncate text-vt-14 font-semibold text-vt-text">{item.title}</div>
        <div className="text-vt-12 text-vt-text-muted">{CATEGORY_LABEL[item.category]}</div>
        {item.location_hint ? (
          <div className="truncate text-vt-12 text-vt-text-soft">{item.location_hint}</div>
        ) : null}
      </div>
    </button>
  );
}

function LostFoundDetail({ item }: { item: LfItem }): JSX.Element {
  const photos = item.photo_storage_keys ?? [];
  return (
    <div className="flex flex-col gap-vt-3">
      {photos.length > 0 ? (
        <div className="grid grid-cols-3 gap-vt-2">
          {photos.map((_, idx) => (
            <div key={idx} className="aspect-square w-full overflow-hidden rounded-vt-sm bg-vt-surface-2">
              <AuthedThumb itemId={item.id} idx={idx} alt={`Photo ${idx + 1}`} />
            </div>
          ))}
        </div>
      ) : null}
      {item.description ? (
        <p className="whitespace-pre-wrap text-vt-13 text-vt-text">{item.description}</p>
      ) : null}
      <dl className="grid grid-cols-2 gap-vt-2 text-vt-12">
        <dt className="text-vt-text-muted">Location</dt>
        <dd className="text-vt-text">{item.location_hint ?? '—'}</dd>
        <dt className="text-vt-text-muted">When</dt>
        <dd className="text-vt-text">{item.incident_date ? formatDateTime(item.incident_date) : '—'}</dd>
        <dt className="text-vt-text-muted">Posted by</dt>
        <dd className="text-vt-text">{item.posted_by_kind}</dd>
        <dt className="text-vt-text-muted">Contact</dt>
        <dd className="text-vt-text">
          {item.contact_name ?? '—'}
          {item.contact_phone_visible ? ' (phone visible)' : ' (via society admin)'}
        </dd>
        {item.expires_at ? (
          <>
            <dt className="text-vt-text-muted">Expires</dt>
            <dd className="text-vt-text">{formatDateTime(item.expires_at)}</dd>
          </>
        ) : null}
        {item.resolution_notes ? (
          <>
            <dt className="text-vt-text-muted">Resolution</dt>
            <dd className="text-vt-text">{item.resolution_notes}</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

interface ThumbProps {
  itemId: string;
  idx: number;
  alt: string;
}

function AuthedThumb({ itemId, idx, alt }: ThumbProps): JSX.Element {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [src, setSrc] = useState<string | null>(null);

  const fetchBlob = async (): Promise<void> => {
    try {
      const res = await api.get(`/lost-found/${itemId}/photos/${idx}`, { responseType: 'blob' });
      setSrc(URL.createObjectURL(res.data as Blob));
    } catch {
      setSrc(null);
    }
  };

  useState(() => {
    if (accessToken) void fetchBlob();
    return undefined;
  });

  if (!src) {
    return (
      <div className="flex h-full w-full items-center justify-center text-vt-text-muted">
        <HelpCircle className="h-6 w-6" />
      </div>
    );
  }
  return <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy" />;
}
